import type {
  BehaviorChangeSeverity,
  CircleConcernsData
} from '@/types/iris-v2';
import { isAfter, parseISO } from 'date-fns';

/**
 * Stopgap in-app alerts for the people the user watches over, derived from
 * `GET /api/v1/friends/circle/concerns`.
 *
 * That endpoint returns every accepted circle member in both directions, so
 * callers filter it down to the watched-over set: the friendship ids from
 * `GET /api/v1/friends/received` (connections the other person started). Both
 * endpoints read the same friendship row, so `friendshipId` joins them.
 *
 * Seen state is per person in the UI, but the backend only stores a single
 * `lastSeenAt` per user (moved forward by `POST .../concerns/seen`). So an
 * item is new when it's after `lastSeenAt` AND not in the browser's set of
 * locally seen keys (`circle-alerts-seen-store.ts`), and `lastSeenAt` only
 * advances to the watermark below which everything has been seen — never far
 * enough to clear another person's unopened alerts. Both behavior changes and
 * flagged messages count as alert items, matching the Iris circle-concerns
 * card. This module is replaced by the per-watcher `/api/v1/notifications`
 * endpoints once they ship.
 */

type CircleAlertBase = {
  key: string;
  friendshipId?: number;
  memberName: string;
  occurredAt?: string;
  isNew: boolean;
};

export type CircleAlertItem =
  | (CircleAlertBase & {
      kind: 'behavior';
      severity?: BehaviorChangeSeverity;
      changeType?: string;
      description?: string;
    })
  | (CircleAlertBase & {
      kind: 'flagged';
      sender?: string;
      subject?: string;
      status?: string;
      channel?: string;
    });

const isNewerThan = (occurredAt?: string, lastSeenAt?: string): boolean => {
  if (!occurredAt) {
    return false;
  }
  if (!lastSeenAt) {
    return true;
  }
  return isAfter(parseISO(occurredAt), parseISO(lastSeenAt));
};

const occurredTime = (item: CircleAlertItem): number =>
  item.occurredAt ? parseISO(item.occurredAt).getTime() : 0;

const NONE: ReadonlySet<string> = new Set();

/**
 * Flattens the per-member concerns response into one list, newest first,
 * keeping only members whose friendship is in `watchedFriendshipIds`.
 */
export const toCircleAlertItems = (
  data: CircleConcernsData | undefined,
  watchedFriendshipIds: ReadonlySet<number>,
  locallySeenKeys: ReadonlySet<string> = NONE
): CircleAlertItem[] => {
  if (!data?.members) {
    return [];
  }
  const { lastSeenAt } = data;
  const watched = data.members.filter(
    (member) =>
      member.friendshipId !== undefined &&
      watchedFriendshipIds.has(member.friendshipId)
  );
  const items: CircleAlertItem[] = watched.flatMap((member) => {
    const base = {
      friendshipId: member.friendshipId,
      memberName: member.displayName ?? ''
    };
    return [
      ...(member.behaviorChanges ?? []).map(
        (change): CircleAlertItem => ({
          ...base,
          kind: 'behavior',
          key: `behavior-${change.id}`,
          occurredAt: change.occurredAt,
          isNew:
            isNewerThan(change.occurredAt, lastSeenAt) &&
            !locallySeenKeys.has(`behavior-${change.id}`),
          severity: change.severity,
          changeType: change.changeType,
          description: change.description
        })
      ),
      ...(member.flaggedAnalyses ?? []).map(
        (analysis): CircleAlertItem => ({
          ...base,
          kind: 'flagged',
          key: `flagged-${analysis.uuid}`,
          occurredAt: analysis.occurredAt,
          isNew:
            isNewerThan(analysis.occurredAt, lastSeenAt) &&
            !locallySeenKeys.has(`flagged-${analysis.uuid}`),
          sender: analysis.sender,
          subject: analysis.subjectOrSnippet,
          status: analysis.status,
          channel: analysis.channel
        })
      )
    ];
  });
  return items.sort((a, b) => occurredTime(b) - occurredTime(a));
};

const newestOccurredAt = (items: CircleAlertItem[]): string | undefined =>
  items.reduce<CircleAlertItem | undefined>(
    (newest, item) =>
      item.occurredAt && (!newest || occurredTime(item) > occurredTime(newest))
        ? item
        : newest,
    undefined
  )?.occurredAt;

/** Unseen alert count per watched person, keyed by friendship id. */
export const countUnseenByFriendship = (
  items: CircleAlertItem[]
): Map<number, number> => {
  const counts = new Map<number, number>();
  for (const item of items) {
    if (item.isNew && item.friendshipId !== undefined) {
      counts.set(item.friendshipId, (counts.get(item.friendshipId) ?? 0) + 1);
    }
  }
  return counts;
};

/**
 * How far `lastSeenAt` can move without clearing anything still unseen: the
 * newest seen item that is older than every unseen one. Returned as that
 * item's own timestamp string, passed through unchanged — using "now" would
 * also clear items created after the fetch that nobody has looked at.
 * `undefined` when it wouldn't move `lastSeenAt` forward.
 */
export const seenWatermark = (
  items: CircleAlertItem[],
  lastSeenAt: string | undefined
): string | undefined => {
  const unseenTimes = items
    .filter((item) => item.isNew && item.occurredAt)
    .map(occurredTime);
  const oldestUnseen =
    unseenTimes.length > 0
      ? Math.min(...unseenTimes)
      : Number.POSITIVE_INFINITY;
  const watermark = newestOccurredAt(
    items.filter((item) => !item.isNew && occurredTime(item) < oldestUnseen)
  );
  if (!watermark) {
    return undefined;
  }
  if (lastSeenAt && !isAfter(parseISO(watermark), parseISO(lastSeenAt))) {
    return undefined;
  }
  return watermark;
};

/**
 * The locally seen keys still worth remembering: items the server doesn't
 * already treat as seen (after `lastSeenAt`) that are still in the response.
 * Keeps the stored set from growing without bound.
 */
export const prunedSeenKeys = (
  items: CircleAlertItem[],
  seenKeys: ReadonlySet<string>,
  lastSeenAt: string | undefined
): Set<string> =>
  new Set(
    items
      .filter(
        (item) =>
          seenKeys.has(item.key) && isNewerThan(item.occurredAt, lastSeenAt)
      )
      .map((item) => item.key)
  );

/** Alert items per watched person, keyed by friendship id, newest first. */
export const groupByFriendship = (
  items: CircleAlertItem[]
): Map<number, CircleAlertItem[]> => {
  const groups = new Map<number, CircleAlertItem[]>();
  for (const item of items) {
    if (item.friendshipId !== undefined) {
      groups.set(item.friendshipId, [
        ...(groups.get(item.friendshipId) ?? []),
        item
      ]);
    }
  }
  return groups;
};
